import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Search,
  Calendar,
  X,
  Printer,
  CheckCircle,
  Clock,
  MapPin,
  FileText,
  TrendingUp,
  CreditCard,
  Fuel,
  DollarSign,
  User,
  Car,
} from "lucide-react";
import api from "../api/Api";
import { toast } from "sonner";

const DmrHistory = () => {
  const navigate = useNavigate();
  const { driverRegNo } = useParams();

  const [historyData, setHistoryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [selectedDmr, setSelectedDmr] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchHistory = async () => {
      const regNo = driverRegNo || localStorage.getItem("driverRegNo");

      if (!regNo || regNo === "undefined" || regNo === "null") {
        toast.error("Error: Driver Registration Number is missing.");
        setLoading(false);
        return;
      }

      try {
        const res = await api.get(`/dmr/dmr-history/${regNo}`);
        if (res.data.success) {
          const rawData = res.data.data;
          // console.log("HISTORY DATA FROM API:", rawData[0]);

          const uniqueDataMap = rawData.reduce((acc, current) => {
            if (!acc[current.id]) {
              acc[current.id] = current;
            }
            return acc;
          }, {});

          const cleanUniqueData = Object.values(uniqueDataMap);
          setHistoryData(cleanUniqueData);
          setFilteredData(cleanUniqueData);
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load history");
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [driverRegNo]);

  // FIXED FILTER LOGIC
  useEffect(() => {
    let result = [...historyData];

    // Search filter - check DMR number
    if (searchQuery.trim()) {
      result = result.filter(
        (d) =>
          d.dmr_no &&
          d.dmr_no.toLowerCase().includes(searchQuery.toLowerCase().trim()),
      );
    }

    // Date From filter
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      result = result.filter((d) => {
        if (!d.created_date) return false;
        const recordDate = new Date(d.created_date);
        recordDate.setHours(0, 0, 0, 0);
        return recordDate >= fromDate;
      });
    }

    // Date To filter
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter((d) => {
        if (!d.created_date) return false;
        const recordDate = new Date(d.created_date);
        return recordDate <= toDate;
      });
    }

    setFilteredData(result);
  }, [searchQuery, dateFrom, dateTo, historyData]);

  const openModal = (dmr) => {
    setSelectedDmr(dmr);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedDmr(null), 300);
    document.body.style.overflow = "auto";
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Clear filters function
  const clearFilters = () => {
    setSearchQuery("");
    setDateFrom("");
    setDateTo("");
  };

  const hasActiveFilters = searchQuery || dateFrom || dateTo;

  // --- MATH LOGIC ---
  let systemCalculatedFuel = 0,
    misc2Amt = 0,
    misc3Amt = 0;
  let dbAdvance = 0,
    dbRefuel = 0,
    dbToll = 0,
    dbParking = 0,
    dbNight = 0,
    dbFood = 0;
  let cashFuel = 0,
    cashToll = 0,
    cashParking = 0,
    cashNight = 0,
    cashFood = 0,
    totalCashExp = 0;
  let officePrepaid = 0,
    guestCol = 0,
    totalCollection = 0,
    totalExpenses = 0,
    paidToOffice = 0;

  if (selectedDmr) {
    // 1. COLLECTIONS (Office Paid Allowances + Guest Collection)
    dbAdvance = parseFloat(selectedDmr.advance_from_ofc) || 0;
    dbRefuel = parseFloat(selectedDmr.office_fuel_amnt) || 0;

    // Using new backend fields (with fallback to old ones just in case)
    dbToll =
      parseFloat(selectedDmr.office_toll_amnt) ||
      parseFloat(selectedDmr.toll_amnt) ||
      0;
    dbParking =
      parseFloat(selectedDmr.office_parking_amnt) ||
      parseFloat(selectedDmr.parking_amnt) ||
      0;
    dbNight =
      parseFloat(selectedDmr.office_night_amnt) ||
      parseFloat(selectedDmr.night_amnt) ||
      0;
    dbFood =
      parseFloat(selectedDmr.office_food_amnt) ||
      parseFloat(selectedDmr.food_amnt) ||
      0;

    officePrepaid =
      dbAdvance + dbRefuel + dbToll + dbParking + dbNight + dbFood;
    guestCol = parseFloat(selectedDmr.collection_from_guest) || 0;
    totalCollection = officePrepaid + guestCol;

    // 2. EXPENSES (System Calculated Fuel + Driver Cash + Manual Misc)
    cashFuel = parseFloat(selectedDmr.cash_fuel_amnt) || 0;
    cashToll = parseFloat(selectedDmr.cash_toll_amnt) || 0;
    cashParking = parseFloat(selectedDmr.cash_parking_amnt) || 0;
    cashNight = parseFloat(selectedDmr.cash_night_amnt) || 0;
    cashFood = parseFloat(selectedDmr.cash_food_amnt) || 0;
    totalCashExp =cashToll + cashParking + cashNight + cashFood;

    systemCalculatedFuel = parseFloat(selectedDmr.calc_fuel_amnt) || 0;
    misc2Amt = parseFloat(selectedDmr.miscval2) || 0;
    misc3Amt = parseFloat(selectedDmr.miscval3) || 0;

    totalExpenses = systemCalculatedFuel + misc2Amt + misc3Amt + totalCashExp;

    // 3. FINAL SETTLEMENT
    paidToOffice = totalCollection - totalExpenses;
  }

  return (
    <div
      className="min-vh-100"
      style={{
        backgroundColor: "#f8f9fa",
        fontFamily:
          "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        
        * { font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; }
        
        .native-input { 
          background-color: rgba(255,255,255,0.95); 
          border: 1px solid rgba(255,255,255,0.3); 
          border-radius: 12px; 
          padding: 10px 14px; 
          font-size: 14px; 
          color: #1a1a1a; 
          transition: all 0.2s ease;
          backdrop-filter: blur(10px);
        }
        .native-input:focus { 
          border-color: rgba(255,255,255,0.6); 
          outline: none; 
          background-color: rgba(255,255,255,1);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .native-input::placeholder { color: rgba(0,0,0,0.4); }
        
        .search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: rgba(0,0,0,0.4); z-index: 1; }
        .input-with-icon { padding-left: 40px; }
        
        .history-card { 
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          border-radius: 16px; 
          box-shadow: 0 1px 6px rgba(0,0,0,0.05); 
          padding: 14px 16px; 
          margin-bottom: 10px; 
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer; 
          border: 1px solid rgba(0,0,0,0.04);
          position: relative;
          overflow: hidden;
        }
        .history-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #10b981, #059669);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .history-card:hover::before { opacity: 1; }
        .history-card:hover { 
          transform: translateY(-2px); 
          box-shadow: 0 6px 20px rgba(16,185,129,0.12);
        }
        .history-card:active { transform: translateY(-1px) scale(0.99); }

        .status-badge {
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.3px;
          text-transform: uppercase;
        }

        .clear-filter-btn {
          background: rgba(255,255,255,0.2);
          border: 1px solid rgba(255,255,255,0.3);
          color: white;
          border-radius: 10px;
          padding: 6px 12px;
          font-size: 12px;
          font-weight: 600;
          transition: all 0.2s ease;
        }
        .clear-filter-btn:hover {
          background: rgba(255,255,255,0.3);
          border-color: rgba(255,255,255,0.5);
        }

        .bottom-sheet-backdrop { 
          position: fixed; 
          top: 0; 
          left: 0; 
          right: 0; 
          bottom: 0; 
          background: rgba(0,0,0,0.5); 
          backdrop-filter: blur(8px); 
          z-index: 1040; 
          opacity: 0; 
          pointer-events: none; 
          transition: opacity 0.3s ease; 
        }
        .bottom-sheet-backdrop.show { opacity: 1; pointer-events: auto; }

        .bottom-sheet { 
          position: fixed; 
          bottom: 0; 
          left: 0; 
          right: 0; 
          background: #ffffff; 
          border-radius: 24px 24px 0 0; 
          z-index: 1050; 
          transform: translateY(100%); 
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1); 
          max-height: 90%; 
          display: flex; 
          flex-direction: column;
          box-shadow: 0 -10px 40px rgba(0,0,0,0.2);
        }
        .bottom-sheet.show { transform: translateY(0); }
        .sheet-handle {
          width: 36px;
          height: 4px;
          background: #d1d5db;
          border-radius: 2px;
          margin: 10px auto 6px;
        }
        .sheet-body { 
          overflow-y: auto; 
          padding: 16px 20px 20px; 
          -webkit-overflow-scrolling: touch;
        }
        
        .info-card {
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          border: 1px solid #86efac;
          border-radius: 14px;
          padding: 12px;
        }

        .metric-box {
          background: #ffffff;
          border-radius: 14px;
          padding: 12px;
          box-shadow: 0 1px 6px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
          border: 1px solid rgba(0,0,0,0.04);
          height: 100%;
        }
        .metric-box:hover {
          box-shadow: 0 3px 10px rgba(0,0,0,0.08);
          transform: translateY(-1px);
        }

        .odo-box {
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          padding: 10px;
          text-align: center;
          transition: all 0.2s ease;
        }
        .odo-box-total {
          background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
          border-color: #60a5fa;
          box-shadow: 0 3px 10px rgba(59, 130, 246, 0.15);
        }

        .net-payment-card {
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          border-radius: 16px;
          padding: 16px 18px;
          box-shadow: 0 6px 20px rgba(0,0,0,0.15);
        }

        .print-btn {
          background: linear-gradient(90deg, #3b82f6 0%, #06b6d4 100%);
          border: none;
          border-radius: 14px;
          padding: 14px 20px;
          font-weight: 700;
          font-size: 15px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 14px rgba(59, 130, 246, 0.3);
        }
        .print-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
        }
        .print-btn:active {
          transform: translateY(0);
        }

        .skeleton-loader {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: loading 1.5s infinite;
          border-radius: 12px;
        }
        @keyframes loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        
        .print-receipt-container { display: none; }
        @media print {
          @page { size: A4; margin: 0mm; } 
          body * { visibility: hidden !important; }
          .no-print { display: none !important; }
          
          /* Force container to stay on one page without extra margins */
          .print-receipt-container { 
            display: block !important; 
            position: absolute; 
            left: 0; 
            top: 0; 
            width: 100%; 
            box-sizing: border-box;
            visibility: visible !important; 
            color: #000; 
            padding: 15mm;
          }
          .print-receipt-container * { visibility: visible !important; }
          
          .print-header {
            border-bottom: 3px solid #10b981;
            padding-bottom: 10px;
            margin-bottom: 15px;
          }
          .print-company-name {
            color: #ff6600 !important;
            font-size: 24px !important; 
            font-weight: 900 !important;
            letter-spacing: 0.5px;
          }
          .print-title {
            background: linear-gradient(135deg, #10b981, #059669) !important;
            color: white !important;
            padding: 6px 12px;
            border-radius: 6px;
            display: inline-block;
            font-weight: 700;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .print-info-table {
            border: 2px solid #e5e7eb !important;
            border-radius: 8px;
            overflow: hidden;
            -webkit-print-color-adjust: exact;
          }
          .print-info-table td {
            padding: 8px !important;
            border: 1px solid #e5e7eb !important;
          }
          .print-info-table .label-cell {
            background-color: #f3f4f6 !important;
            font-weight: 700 !important;
            width: 20%;
            -webkit-print-color-adjust: exact;
          }
          
          .print-odo-container {
            display: flex;
            justify-content: center;
            gap: 20px;
            margin: 15px 0; 
          }
          .print-odo-box {
            border: 2px solid #cbd5e1 !important;
            border-radius: 10px !important;
            padding: 10px 15px !important;
            text-align: center !important;
            min-width: 120px;
            background: #f8fafc !important;
            -webkit-print-color-adjust: exact;
          }
          .print-odo-label {
            color: #64748b !important;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            margin-bottom: 2px;
          }
          .print-odo-value {
            font-size: 20px !important;
            font-weight: 800 !important;
            color: #1e293b !important;
          }
          .print-odo-box-total {
            background: linear-gradient(135deg, #dbeafe, #bfdbfe) !important;
            border-color: #3b82f6 !important;
            -webkit-print-color-adjust: exact;
          }
          .print-odo-box-total .print-odo-value {
            color: #1d4ed8 !important;
          }
          
          .print-section {
            background: #f9fafb !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 10px;
            padding: 12px;
            margin-bottom: 10px;
            -webkit-print-color-adjust: exact;
          }
          .print-section-title {
            font-weight: 700;
            font-size: 14px;
            color: #374151 !important;
            border-bottom: 2px solid #d1d5db !important;
            padding-bottom: 6px;
            margin-bottom: 8px;
          }
          .print-amount-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            font-size: 12px;
          }
          .print-amount-row.total {
            border-top: 2px solid #9ca3af !important;
            padding-top: 8px;
            margin-top: 6px;
            font-weight: 700 !important;
            font-size: 13px;
          }
          .print-amount-row.total.collections { color: #059669 !important; }
          .print-amount-row.total.expenses { color: #dc2626 !important; }
          
          .print-net-payment {
            background: linear-gradient(135deg, #1e293b, #334155) !important;
            color: white !important;
            border-radius: 12px;
            padding: 15px;
            margin: 15px 0;
            display: flex;
            justify-content: space-between;
            align-items: center;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print-net-payment-label {
            font-weight: 700;
            font-size: 14px;
          }
          .print-net-payment-amount {
            font-size: 24px !important;
            font-weight: 900 !important;
          }
          
          .print-signatures {
            display: flex;
            justify-content: space-between;
            margin-top: 35px;
            padding-top: 10px;
          }
          .print-signature-box {
            text-align: center;
            flex: 1;
          }
          .print-signature-line {
            border-top: 2px solid #000;
            padding-top: 6px;
            margin: 0 10px;
            font-size: 11px;
            font-weight: 600;
          }
        }
      `}</style>

      {/* HEADER */}
      <div
        className="no-print"
        style={{
          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
          paddingTop: "12px",
          paddingBottom: "14px",
          boxShadow: "0 4px 20px rgba(16, 185, 129, 0.3)",
        }}
      >
        <div className="container-fluid px-3">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="btn btn-link text-white p-0"
                style={{ textDecoration: "none" }}
              >
                <ChevronLeft size={26} strokeWidth={2.5} />
              </button>
              <div>
                <h2
                  className="text-white m-0 fw-bold"
                  style={{ fontSize: "22px", letterSpacing: "-0.3px" }}
                >
                  DMR History
                </h2>
                <p className="text-white-50 m-0" style={{ fontSize: "12px" }}>
                  Track your receipts
                </p>
              </div>
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="clear-filter-btn d-flex align-items-center gap-1"
              >
                <X size={14} /> Clear
              </button>
            )}
          </div>

          <div className="position-relative mb-2">
            <Search size={16} className="search-icon" />
            <input
              type="text"
              className="w-100 native-input input-with-icon"
              placeholder="Search DMR number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="row g-2">
            <div className="col-6">
              <div className="position-relative">
                <Calendar
                  size={14}
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "rgba(0,0,0,0.4)",
                    zIndex: 1,
                  }}
                />
                <input
                  type="date"
                  className="w-100 native-input"
                  style={{ paddingLeft: "34px", fontSize: "13px" }}
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  placeholder="From"
                />
              </div>
            </div>
            <div className="col-6">
              <div className="position-relative">
                <Calendar
                  size={14}
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "rgba(0,0,0,0.4)",
                    zIndex: 1,
                  }}
                />
                <input
                  type="date"
                  className="w-100 native-input"
                  style={{ paddingLeft: "34px", fontSize: "13px" }}
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  placeholder="To"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* LIST CONTENT */}
      <div className="container-fluid px-3 py-3 no-print">
        {loading ? (
          <div className="text-center mt-5">
            <div
              className="spinner-border text-success"
              role="status"
              style={{ width: "2.5rem", height: "2.5rem" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <p
              className="text-secondary mt-3 fw-medium"
              style={{ fontSize: "14px" }}
            >
              Loading history...
            </p>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="text-center mt-5">
            <div className="mb-3" style={{ opacity: 0.2 }}>
              <FileText size={56} strokeWidth={1.5} />
            </div>
            <h6 className="fw-bold text-dark mb-2">No Records Found</h6>
            <p className="text-muted small">
              Try adjusting your search or filters
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="btn btn-sm btn-outline-success mt-2"
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <p
                className="text-muted mb-0 fw-medium"
                style={{ fontSize: "13px" }}
              >
                <TrendingUp size={14} className="me-1" />
                {filteredData.length}{" "}
                {filteredData.length === 1 ? "record" : "records"}
              </p>
            </div>

            {filteredData.map((dmr) => (
              <div
                key={dmr.id}
                className="history-card"
                onClick={() => openModal(dmr)}
              >
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <div
                      className="fw-bold text-dark mb-1"
                      style={{ fontSize: "15px" }}
                    >
                      {dmr.dmr_no}
                    </div>
                    <div
                      className="d-flex align-items-center gap-2 text-secondary"
                      style={{ fontSize: "12px" }}
                    >
                      <Calendar size={12} />
                      <span>{formatDate(dmr.created_date)}</span>
                    </div>
                  </div>
                  <span
                    className={`status-badge ${
                      dmr.is_verified === 1
                        ? "bg-success bg-opacity-10 text-success"
                        : "bg-warning bg-opacity-10 text-warning"
                    }`}
                  >
                    {dmr.is_verified === 1 ? (
                      <>
                        <CheckCircle size={10} className="me-1" />
                        Approved
                      </>
                    ) : (
                      <>
                        <Clock size={10} className="me-1" />
                        Pending
                      </>
                    )}
                  </span>
                </div>

                <div className="row g-2">
                  <div className="col-6">
                    <div
                      style={{
                        background: "#f0fdf4",
                        borderRadius: "8px",
                        padding: "8px",
                        border: "1px solid #86efac",
                      }}
                    >
                      <div
                        className="small text-success mb-1 fw-semibold"
                        style={{ fontSize: "11px" }}
                      >
                        <MapPin size={11} className="me-1" />
                        Distance
                      </div>
                      <div
                        className="fw-bold text-dark"
                        style={{ fontSize: "16px" }}
                      >
                        {dmr.total_km} km
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div
                      style={{
                        background: "#fef3c7",
                        borderRadius: "8px",
                        padding: "8px",
                        border: "1px solid #fde047",
                      }}
                    >
                      <div
                        className="small text-warning mb-1 fw-semibold"
                        style={{ fontSize: "11px" }}
                      >
                        <DollarSign size={11} className="me-1" />
                        Paid
                      </div>
                      <div
                        className="fw-bold text-dark"
                        style={{ fontSize: "16px" }}
                      >
                        ₹{parseFloat(dmr.paid_ofc || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* MODAL / BOTTOM SHEET */}
      <div
        className={`bottom-sheet-backdrop no-print ${isModalOpen ? "show" : ""}`}
        onClick={closeModal}
      ></div>
      <div className={`bottom-sheet no-print ${isModalOpen ? "show" : ""}`}>
        <div className="sheet-handle"></div>

        {/* HEADER WITH INLINE PRINT BUTTON */}
        <div
          className="px-3 py-3 d-flex justify-content-between align-items-center"
          style={{ borderBottom: "1px solid #f0f0f0" }}
        >
          <h6 className="m-0 fw-bold" style={{ fontSize: "18px" }}>
            DMR Details
          </h6>
          <div className="d-flex align-items-center gap-2">
            {/* Conditional Print Button */}
            {selectedDmr &&
              (selectedDmr.is_verified === 1 ||
                selectedDmr.dmr_status === 1) && (
                <button
                  onClick={handlePrint}
                  className="btn d-flex align-items-center gap-2 rounded-pill px-3 py-1 shadow-sm"
                  style={{
                    background:
                      "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                    color: "white",
                    fontSize: "13px",
                    fontWeight: "600",
                    border: "none",
                  }}
                >
                  <Printer size={14} /> Print
                </button>
              )}
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="btn btn-light rounded-circle p-2"
              style={{
                width: "36px",
                height: "36px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {selectedDmr && (
          <div className="sheet-body">
            {/* Guest Info Card */}
            <div
              className="mb-3 rounded-4 shadow-sm"
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid #e2e8f0",
                overflow: "hidden",
              }}
            >
              {/* TOP: Guest & Vehicle Info */}
              <div className="p-3">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center bg-success bg-opacity-10 text-success flex-shrink-0"
                    style={{ width: "46px", height: "46px" }}
                  >
                    <User size={22} strokeWidth={2.5} />
                  </div>

                  <div className="flex-grow-1">
                    <h6
                      className="fw-bold mb-1 text-dark"
                      style={{ fontSize: "16px", letterSpacing: "-0.2px" }}
                    >
                      {selectedDmr.guest_name}
                    </h6>
                    <div
                      className="d-flex align-items-center flex-wrap gap-2 text-secondary"
                      style={{ fontSize: "13px" }}
                    >
                      <div className="d-flex align-items-center gap-1">
                        <Car size={14} className="text-primary" />
                        <span className="fw-medium text-dark">
                          {selectedDmr.carmodel_name}
                        </span>
                      </div>
                      <span className="text-muted" style={{ fontSize: "10px" }}>
                        •
                      </span>
                      <span className="text-muted fw-medium">
                        {selectedDmr.vehicle_no}
                      </span>
                      <span className="text-muted" style={{ fontSize: "10px" }}>
                        •
                      </span>
                      <span className="text-muted fw-medium">
                        {selectedDmr.millege} km/l
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* TICKET DIVIDER */}
              <div className="position-relative" style={{ height: "1px" }}>
                <div
                  style={{
                    borderTop: "2px dashed #e2e8f0",
                    position: "absolute",
                    top: "0",
                    left: "16px",
                    right: "16px",
                  }}
                ></div>
                <div
                  className="position-absolute"
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    left: "-8px",
                    top: "-8px",
                    backgroundColor: "#f8f9fa",
                    boxShadow: "inset -1px 0 2px rgba(0,0,0,0.04)",
                  }}
                ></div>
                <div
                  className="position-absolute"
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    right: "-8px",
                    top: "-8px",
                    backgroundColor: "#f8f9fa",
                    boxShadow: "inset 1px 0 2px rgba(0,0,0,0.04)",
                  }}
                ></div>
              </div>

              {/* BOTTOM: Timeline Period */}
              <div className="p-3" style={{ backgroundColor: "#f8fafc" }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex flex-column text-start">
                    <span
                      className="text-uppercase text-muted fw-bold mb-1"
                      style={{ fontSize: "10px", letterSpacing: "0.5px" }}
                    >
                      Start
                    </span>
                    <span
                      className="fw-bold text-dark"
                      style={{ fontSize: "14px" }}
                    >
                      {formatDate(selectedDmr.arrive_dt)}
                    </span>
                    {selectedDmr.arrive_time && (
                      <span
                        className="text-success fw-semibold"
                        style={{ fontSize: "12px" }}
                      >
                        {selectedDmr.arrive_time}
                      </span>
                    )}
                  </div>

                  <div className="flex-grow-1 px-3 d-flex align-items-center position-relative">
                    <div
                      className="w-100"
                      style={{
                        height: "2px",
                        backgroundColor: "#cbd5e1",
                        borderRadius: "2px",
                      }}
                    ></div>
                    <div
                      className="position-absolute top-50 start-50 translate-middle bg-white px-1 text-secondary rounded-circle"
                      style={{ padding: "2px" }}
                    >
                      <Clock size={14} />
                    </div>
                  </div>

                  <div className="d-flex flex-column text-end">
                    <span
                      className="text-uppercase text-muted fw-bold mb-1"
                      style={{ fontSize: "10px", letterSpacing: "0.5px" }}
                    >
                      End
                    </span>
                    <span
                      className="fw-bold text-dark"
                      style={{ fontSize: "14px" }}
                    >
                      {formatDate(selectedDmr.dept_dt)}
                    </span>
                    {selectedDmr.dept_time && (
                      <span
                        className="text-danger fw-semibold"
                        style={{ fontSize: "12px" }}
                      >
                        {selectedDmr.dept_time}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Odometer & Compact Fuel Logic (Modal) */}
            <div className="mb-3">
              <div className="d-flex align-items-center gap-2 mb-2">
                <MapPin size={16} className="text-primary" />
                <span
                  className="fw-bold text-dark"
                  style={{ fontSize: "14px" }}
                >
                  Odometer & Fuel
                </span>
              </div>
              <div className="row g-2 text-center mb-2">
                <div className="col-4">
                  <div className="odo-box">
                    <div
                      className="small text-secondary fw-semibold mb-1"
                      style={{ fontSize: "10px" }}
                    >
                      START ODO(KM)
                    </div>
                    <div
                      className="fw-bold text-dark"
                      style={{ fontSize: "16px" }}
                    >
                      {selectedDmr.start_km}
                    </div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="odo-box">
                    <div
                      className="small text-secondary fw-semibold mb-1"
                      style={{ fontSize: "10px" }}
                    >
                      END ODO(KM)
                    </div>
                    <div
                      className="fw-bold text-dark"
                      style={{ fontSize: "16px" }}
                    >
                      {selectedDmr.end_km}
                    </div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="odo-box odo-box-total">
                    <div
                      className="small text-primary fw-bold mb-1"
                      style={{ fontSize: "10px" }}
                    >
                      TOTAL(KM)
                    </div>
                    <div
                      className="fw-bold text-primary"
                      style={{ fontSize: "16px" }}
                    >
                      {selectedDmr.total_km}
                    </div>
                  </div>
                </div>
              </div>

              {/* Compact Math Breakdown */}
              <div
                className="rounded px-2 py-2"
                style={{
                  backgroundColor: "#f8fafc",
                  border: "1px dashed #cbd5e1",
                }}
              >
                <div
                  className="d-flex flex-wrap justify-content-between align-items-center"
                  style={{ fontSize: "11px" }}
                >
                  <div className="text-secondary">
                    Mileage:{" "}
                    <span className="fw-bold text-dark">
                      {selectedDmr.millege} km/L
                    </span>{" "}
                    | Rate:{" "}
                    <span className="fw-bold text-dark">
                      ₹{selectedDmr.oil_rate || 0}/L
                    </span>
                  </div>
                  <div className="text-muted mt-1 mt-sm-0">
                    ({selectedDmr.total_km} ÷ {selectedDmr.millege}) × ₹
                    {selectedDmr.oil_rate || 0} ={" "}
                    <span
                      className="fw-bolder text-primary"
                      style={{ fontSize: "12px" }}
                    >
                      ₹{systemCalculatedFuel.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Collections & Expenses */}
            <div className="row g-2 mb-3 align-items-stretch">
              {/* COLUMN 1: Collections */}
              <div className="col-6">
                <div className="metric-box h-100 d-flex flex-column">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div
                      className="bg-success bg-opacity-10 rounded d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: "28px", height: "28px" }}
                    >
                      <CreditCard size={14} className="text-success" />
                    </div>
                    <span
                      className="fw-bold text-dark"
                      style={{ fontSize: "13px" }}
                    >
                      Collections
                    </span>
                  </div>

                  <div
                    className="flex-grow-1 mb-2 pb-2"
                    style={{ borderBottom: "1px dashed #e5e7eb" }}
                  >
                    <div
                      className="d-flex justify-content-between mb-1"
                      style={{ fontSize: "11px" }}
                    >
                      <span className="text-secondary">Duty Adv.(Office)</span>
                      <span className="fw-semibold">
                        ₹{dbAdvance.toFixed(0)}
                      </span>
                    </div>
                    <div
                      className="d-flex justify-content-between mb-1"
                      style={{ fontSize: "11px" }}
                    >
                      <span className="text-secondary">
                        Refuel Amt.(Office)
                      </span>
                      <span className="fw-semibold">
                        ₹{dbRefuel.toFixed(0)}
                      </span>
                    </div>
                    {dbToll + dbParking > 0 && (
                      <div
                        className="d-flex justify-content-between mb-1"
                        style={{ fontSize: "11px" }}
                      >
                        <span className="text-secondary">
                          Toll/Parking(Office)
                        </span>
                        <span className="fw-semibold">
                          ₹{(dbToll + dbParking).toFixed(0)}
                        </span>
                      </div>
                    )}
                    {dbNight + dbFood > 0 && (
                      <div
                        className="d-flex justify-content-between mb-1"
                        style={{ fontSize: "11px" }}
                      >
                        <span className="text-secondary">
                          Night/Food(Office)
                        </span>
                        <span className="fw-semibold">
                          ₹{(dbNight + dbFood).toFixed(0)}
                        </span>
                      </div>
                    )}
                    <div
                      className="d-flex justify-content-between"
                      style={{ fontSize: "11px" }}
                    >
                      <span className="text-secondary">Guest Col.</span>
                      <span className="fw-semibold">
                        ₹{guestCol.toFixed(0)}
                      </span>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-auto pt-1">
                    <span
                      className="fw-bold text-success"
                      style={{ fontSize: "12px" }}
                    >
                      Total
                    </span>
                    <span
                      className="fw-bold text-success"
                      style={{ fontSize: "16px" }}
                    >
                      ₹{totalCollection.toFixed(0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* COLUMN 2: Expenses */}
              <div className="col-6">
                <div className="metric-box h-100 d-flex flex-column">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div
                      className="bg-danger bg-opacity-10 rounded d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: "28px", height: "28px" }}
                    >
                      <Fuel size={14} className="text-danger" />
                    </div>
                    <span
                      className="fw-bold text-dark"
                      style={{ fontSize: "13px" }}
                    >
                      Expenses
                    </span>
                  </div>

                  <div
                    className="flex-grow-1 mb-2 pb-2"
                    style={{ borderBottom: "1px dashed #e5e7eb" }}
                  >
                    <div
                      className="d-flex justify-content-between mb-1"
                      style={{ fontSize: "11px" }}
                    >
                      <span className="text-secondary">Calc. Fuel</span>
                      <span className="fw-semibold">
                        ₹{systemCalculatedFuel.toFixed(0)}
                      </span>
                    </div>

                    {/* Render Cash Expenses only if they exist */}
                    {/* {cashFuel > 0 && (
                      <div
                        className="d-flex justify-content-between mb-1"
                        style={{ fontSize: "11px" }}
                      >
                        <span className="text-secondary">
                          Refuel Amt.(Cash)
                        </span>
                        <span className="fw-semibold">
                          ₹{cashFuel.toFixed(0)}
                        </span>
                      </div>
                    )} */}
                    {cashToll > 0 && (
                      <div
                        className="d-flex justify-content-between mb-1"
                        style={{ fontSize: "11px" }}
                      >
                        <span className="text-secondary">Toll Gate(Cash)</span>
                        <span className="fw-semibold">
                          ₹{cashToll.toFixed(0)}
                        </span>
                      </div>
                    )}
                    {cashParking > 0 && (
                      <div
                        className="d-flex justify-content-between mb-1"
                        style={{ fontSize: "11px" }}
                      >
                        <span className="text-secondary">
                          Parking Fee(Cash)
                        </span>
                        <span className="fw-semibold">
                          ₹{cashParking.toFixed(0)}
                        </span>
                      </div>
                    )}
                    {cashNight > 0 && (
                      <div
                        className="d-flex justify-content-between mb-1"
                        style={{ fontSize: "11px" }}
                      >
                        <span className="text-secondary">Night Halt(cash)</span>
                        <span className="fw-semibold">
                          ₹{cashNight.toFixed(0)}
                        </span>
                      </div>
                    )}
                    {cashFood > 0 && (
                      <div
                        className="d-flex justify-content-between mb-1"
                        style={{ fontSize: "11px" }}
                      >
                        <span className="text-secondary">Food Exp(cash)</span>
                        <span className="fw-semibold">
                          ₹{cashFood.toFixed(0)}
                        </span>
                      </div>
                    )}

                    {misc2Amt > 0 && (
                      <div
                        className="d-flex justify-content-between mb-1"
                        style={{ fontSize: "11px" }}
                      >
                        <span className="text-secondary">
                          {selectedDmr.misc2 || "Misc 1"}
                        </span>
                        <span className="fw-semibold">
                          ₹{misc2Amt.toFixed(0)}
                        </span>
                      </div>
                    )}
                    {misc3Amt > 0 && (
                      <div
                        className="d-flex justify-content-between"
                        style={{ fontSize: "11px" }}
                      >
                        <span className="text-secondary">
                          {selectedDmr.misc3 || "Misc 2"}
                        </span>
                        <span className="fw-semibold">
                          ₹{misc3Amt.toFixed(0)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-auto pt-1">
                    <span
                      className="fw-bold text-danger"
                      style={{ fontSize: "12px" }}
                    >
                      Total
                    </span>
                    <span
                      className="fw-bold text-danger"
                      style={{ fontSize: "16px" }}
                    >
                      ₹{totalExpenses.toFixed(0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {cashFuel > 0 && (
              <div 
                className="d-flex justify-content-between align-items-center px-3 py-2 mb-3 rounded-3" 
                style={{ backgroundColor: "#fffbeb", border: "1px dashed #f59e0b" }}
              >
                <span className="fw-bold text-uppercase" style={{ fontSize: "11px", color: "#d97706" }}>
                  * Refuel Amt (Cash)
                </span>
                <span className="fw-bolder tracking-tight" style={{ fontSize: "15px", color: "#d97706" }}>
                  ₹{cashFuel.toFixed(2)}
                </span>
              </div>
            )}

            {/* Net Payment Card */}
            <div className="net-payment-card mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div
                    className="text-white-50 mb-1"
                    style={{ fontSize: "11px" }}
                  >
                    NET AMOUNT
                  </div>
                  <div
                    className="text-white fw-bold"
                    style={{ fontSize: "14px" }}
                  >
                    Paid to Office
                  </div>
                </div>
                <div
                  className="text-white fw-bold"
                  style={{ fontSize: "28px", letterSpacing: "-0.5px" }}
                >
                  ₹{paidToOffice.toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* PRINT CONTAINER */}
      {selectedDmr && (
        <div className="print-receipt-container">
          {/* Header */}
          <div className="print-header text-center">
            <div className="mb-2">
              <span className="print-title">DRIVER MONEY RECEIPT</span>
            </div>
            <div className="mb-1">
              <span className="print-company-name">
                PATRA TRAVELS PVT. LTD.
              </span>
            </div>
            <p className="small text-muted mb-0">
              Plot No. 1151, 1st Floor, Royal Palace Campus
              <br />
              Tankapani Road, Bhubaneswar, Odisha
            </p>
          </div>

          {/* Bill Info */}
          <div
            className="d-flex justify-content-between mb-3 pb-2"
            style={{ borderBottom: "2px solid #e5e7eb" }}
          >
            <div>
              <span className="fw-bold">Bill No:</span>
              <span className="ms-2">{selectedDmr.dmr_no}</span>
            </div>
            <div>
              <span className="fw-bold">Date:</span>
              <span className="ms-2">
                {formatDate(selectedDmr.created_date)}
              </span>
            </div>
          </div>

          {/* Info Table */}
          <table
            className="table print-info-table mb-4"
            style={{ fontSize: "13px" }}
          >
            <tbody>
              <tr>
                <td className="label-cell">Driver</td>
                <td>
                  {selectedDmr.driver_name} ({selectedDmr.driver_regno})
                </td>
                <td className="label-cell">Vehicle</td>
                <td>
                  {selectedDmr.carmodel_name} ({selectedDmr.vehicle_no}) (
                  {selectedDmr.millege} km/l)
                </td>
              </tr>
              <tr>
                <td className="label-cell">Guest</td>
                <td>
                  {selectedDmr.guest_name} ({selectedDmr.guestmob_no})
                </td>
                <td className="label-cell">Period</td>
                <td>
                  {formatDate(selectedDmr.arrive_dt)} {selectedDmr.arrive_time}{" "}
                  ➔ {formatDate(selectedDmr.dept_dt)} {selectedDmr.dept_time}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Odometer & Compact Fuel Logic */}
          <div className="mb-4">
            {/* 1. The Odometer Boxes */}
            <div
              className="print-odo-container"
              style={{ marginBottom: "8px" }}
            >
              <div className="print-odo-box" style={{ flex: 1 }}>
                <div className="print-odo-label">Start Odo(KM)</div>
                <div className="print-odo-value">{selectedDmr.start_km}</div>
              </div>
              <div className="print-odo-box" style={{ flex: 1 }}>
                <div className="print-odo-label">End Odo(KM)</div>
                <div className="print-odo-value">{selectedDmr.end_km}</div>
              </div>
              <div
                className="print-odo-box print-odo-box-total"
                style={{ flex: 1 }}
              >
                <div className="print-odo-label">Total KM</div>
                <div className="print-odo-value">{selectedDmr.total_km}</div>
              </div>
            </div>

            {/* 2. The Compact Math Breakdown Bar */}
            <div
              className="d-flex justify-content-between align-items-center rounded px-3 py-2"
              style={{
                backgroundColor: "#f8fafc",
                border: "1px dashed #cbd5e1",
              }}
            >
              {/* Left Side: Variables */}
              <div
                className="d-flex align-items-center gap-3 text-secondary"
                style={{ fontSize: "11px" }}
              >
                <span>
                  Mileage:{" "}
                  <strong className="text-dark">
                    {selectedDmr.millege} km/L
                  </strong>
                </span>
                <span className="opacity-50">|</span>
                <span>
                  Oil Rate:{" "}
                  <strong className="text-dark">
                    ₹{selectedDmr.oil_rate || 0}/L
                  </strong>
                </span>
              </div>

              {/* Right Side: The Math */}
              <div
                className="d-flex align-items-center gap-2"
                style={{ fontSize: "11px" }}
              >
                <span className="text-secondary">Formula:</span>
                <span className="text-muted">
                  ({selectedDmr.total_km} ÷ {selectedDmr.millege}) × ₹
                  {selectedDmr.oil_rate || 0}
                </span>
                <span
                  className="fw-bolder text-primary"
                  style={{ fontSize: "13px" }}
                >
                  = ₹{systemCalculatedFuel.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Collections & Expenses (EQUAL HEIGHT BOXES) */}
          <div className="row g-3 mb-3 align-items-stretch">
            {/* COLUMN 1: Collections */}
            <div className="col-6">
              <div className="print-section h-100 d-flex flex-column">
                <div className="print-section-title text-dark">
                  Collections (A)
                </div>

                <div className="flex-grow-1">
                  <div className="print-amount-row">
                    <span className="text-secondary">Duty Advance</span>
                    <span className="fw-medium text-dark">
                      ₹{dbAdvance.toFixed(2)}
                    </span>
                  </div>
                  <div className="print-amount-row">
                    <span className="text-secondary">Refuel Amount</span>
                    <span className="fw-medium text-dark">
                      ₹{dbRefuel.toFixed(2)}
                    </span>
                  </div>
                  {dbToll + dbParking > 0 && (
                    <div className="print-amount-row">
                      <span className="text-secondary">Toll & Parking</span>
                      <span className="fw-medium text-dark">
                        ₹{(dbToll + dbParking).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {dbNight + dbFood > 0 && (
                    <div className="print-amount-row">
                      <span className="text-secondary">Night & Food</span>
                      <span className="fw-medium text-dark">
                        ₹{(dbNight + dbFood).toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="print-amount-row">
                    <span className="text-secondary">Guest Collection</span>
                    <span className="fw-medium text-dark">
                      ₹{guestCol.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="print-amount-row total collections mt-auto pt-2">
                  <span>TOTAL COLLECTION</span>
                  <span>₹{totalCollection.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* COLUMN 2: Expenses */}
            <div className="col-6">
              <div className="print-section h-100 d-flex flex-column">
                <div className="print-section-title text-dark">
                  Expenses (B)
                </div>

                <div className="flex-grow-1">
                  <div className="print-amount-row pb-0">
                    <span className="fw-bold text-dark">Calculated Fuel</span>
                    <span className="fw-bold text-dark">
                      ₹{systemCalculatedFuel.toFixed(2)}
                    </span>
                  </div>
                  <div
                    className="d-flex justify-content-between text-muted"
                    style={{
                      fontSize: "10px",
                      marginTop: "0px",
                      marginBottom: "8px",
                    }}
                  ></div>

                  {/* {cashFuel > 0 && (
                    <div className="print-amount-row">
                      <span className="text-secondary">Refuel Amt.(Cash)</span>
                      <span className="fw-medium text-dark">
                        ₹{cashFuel.toFixed(2)}
                      </span>
                    </div>
                  )} */}
                  {cashToll > 0 && (
                    <div className="print-amount-row">
                      <span className="text-secondary">Toll (Cash)</span>
                      <span className="fw-medium text-dark">
                        ₹{cashToll.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {cashParking > 0 && (
                    <div className="print-amount-row">
                      <span className="text-secondary">Parking (Cash)</span>
                      <span className="fw-medium text-dark">
                        ₹{cashParking.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {cashNight > 0 && (
                    <div className="print-amount-row">
                      <span className="text-secondary">Night (Cash)</span>
                      <span className="fw-medium text-dark">
                        ₹{cashNight.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {cashFood > 0 && (
                    <div className="print-amount-row">
                      <span className="text-secondary">Food (Cash)</span>
                      <span className="fw-medium text-dark">
                        ₹{cashFood.toFixed(2)}
                      </span>
                    </div>
                  )}

                  {misc2Amt > 0 && (
                    <div className="print-amount-row">
                      <span className="text-secondary">
                        {selectedDmr.misc2 || "Misc 1"}
                      </span>
                      <span className="fw-medium text-dark">
                        ₹{misc2Amt.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {misc3Amt > 0 && (
                    <div className="print-amount-row">
                      <span className="text-secondary">
                        {selectedDmr.misc3 || "Misc 2"}
                      </span>
                      <span className="fw-medium text-dark">
                        ₹{misc3Amt.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="print-amount-row total expenses mt-auto pt-2">
                  <span>TOTAL EXPENSES</span>
                  <span>₹{totalExpenses.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {cashFuel > 0 && (
            <div 
              className="d-flex justify-content-between align-items-center mb-3 px-3 py-2" 
              style={{ 
                border: "2px dashed #f59e0b", 
                borderRadius: "10px", 
                backgroundColor: "#fffbeb", 
                WebkitPrintColorAdjust: "exact" 
              }}
            >
              <span className="fw-bold text-uppercase" style={{ fontSize: "12px", color: "#d97706" }}>
                * Refuel Amt (Paid by Driver Cash)
              </span>
              <span className="fw-bold" style={{ fontSize: "18px", color: "#d97706" }}>
                ₹{cashFuel.toFixed(2)}
              </span>
            </div>
          )}

          {/* Net Payment */}
          <div className="print-net-payment">
            <div className="print-net-payment-label">
              NET PAID TO OFFICE (A - B)
            </div>
            <div className="print-net-payment-amount">
              ₹{paidToOffice.toFixed(2)}
            </div>
          </div>

          {/* Signatures */}
          <div className="print-signatures">
            <div className="print-signature-box">
              <div className="print-signature-line">Prepared By</div>
            </div>
            <div className="print-signature-box">
              <div className="print-signature-line">Authorized By</div>
            </div>
            <div className="print-signature-box">
              <div className="print-signature-line">Driver Signature</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DmrHistory;